import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useUrlParam } from '../hooks/useUrlParam';
import { OrgStorageKeys, type OrgStorage } from '../lib/storage';
import {
  allNounQueryInfiniteOptions,
  allNounQueryOptions,
  hotelQueryOptions,
} from '../oas/gen/quoruum/@tanstack/react-query.gen';
import type { AllNounBean, AttributeFilterBean, HotelBean } from '../oas/gen/quoruum/types.gen';
import { ListViewPanel } from './ListViewPanel';
import type { ListViewChiclet, ListViewItem } from './ListViewPanel';
import { NounTypeIcon, nounCategory } from './NounTypeIcon';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_RECENT_HOTELS = 25;
const ATTRIBUTE_PARAM = 'attribute';
const SEARCH_PARAM = 'q';

// The generated AttributeFilterBean type only lists some noun types; the cast lets other noun types through.
interface NounChiclet extends ListViewChiclet {
  type: AttributeFilterBean['type'] & 'CITY';
}

interface ChicletRef {
  type: NounChiclet['type'];
  id: string;
}

export function formatHotelAddress(hotel: HotelBean): string {
  const city = [hotel.city?.name, hotel.city?.stateCode, hotel.city?.countryCode]
    .filter(Boolean)
    .join(', ');
  return [hotel.address1, hotel.address2, city, hotel.postalCode].filter(Boolean).join(', ');
}

function hotelToListItem(h: HotelBean): ListViewItem {
  return {
    id: h.id!,
    primaryText: h.name ?? '(unnamed hotel)',
    secondaryText: formatHotelAddress(h),
    icon: <NounTypeIcon category="Hotel" />,
  };
}

// The default query serializer throws on arrays of objects, so attributes are indexed (Spring-style) here.
function serializeNounQuery(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  const add = (name: string, value: string | number | boolean | undefined | null): void => {
    if (value !== undefined && value !== null) params.append(name, String(value));
  };
  for (const [name, value] of Object.entries(query)) {
    if (name === 'attributes' && Array.isArray(value)) {
      (value as AttributeFilterBean[]).forEach((a, i) => {
        add(`attributes[${i}].type`, a.type);
        add(`attributes[${i}].value`, a.value);
      });
    } else if (Array.isArray(value)) {
      (value as string[]).forEach((v) => add(name, v));
    } else {
      add(name, value as string | number | boolean | undefined);
    }
  }
  return params.toString();
}

function parseAttribute(value: string): ChicletRef | undefined {
  const i = value.indexOf(':');
  if (i <= 0 || i === value.length - 1) return undefined;
  return { type: value.slice(0, i) as ChicletRef['type'], id: value.slice(i + 1) };
}

function formatAttribute(ref: ChicletRef): string {
  return `${ref.type}:${ref.id}`;
}

function joinParts(...parts: Array<string | null | undefined>): string | undefined {
  const joined = parts
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ');
  return joined || undefined;
}

function nounToListItem(n: AllNounBean): ListViewItem {
  const city = n.cityBean;
  const cityStateCountry = joinParts(city?.name, city?.stateName, city?.countryName);
  let primaryText = n.name ?? '(unnamed)';
  let secondaryText: string | undefined;
  switch (n.type) {
    case 'CITY':
      secondaryText = joinParts(city?.stateName, city?.countryName);
      break;
    case 'AIRPORT':
      if (n.code?.trim()) primaryText = `${primaryText} (${n.code.trim()})`;
      secondaryText = cityStateCountry;
      break;
    case 'COUNTRY':
    case 'STATE':
      break;
    default:
      // HOTEL and place types
      secondaryText = cityStateCountry;
  }
  return {
    id: n.id!,
    primaryText,
    secondaryText,
    icon: <NounTypeIcon category={nounCategory(n.type)} />,
  };
}

export interface NounListViewPanelProps {
  /** Id of the hotel shown in the detail area. */
  selectedHotelId?: string;
  /** Called when a hotel is clicked. Non-hotel nouns become search chiclets instead. */
  onSelectHotel: (hotelId: string) => void;
  /** Where recently selected hotels are persisted; they are shown while there is no search text or chiclet. */
  storage: OrgStorage;
  children?: ReactNode;
}

/** A ListViewPanel that searches all nouns (hotels, cities, airports, ...) and filters by chiclet. */
export function NounListViewPanel({
  selectedHotelId,
  onSelectHotel,
  storage,
  children,
}: NounListViewPanelProps): ReactElement {
  const [recentHotelIds, setRecentHotelIds] = useState(
    () => storage.get(OrgStorageKeys.RecentHotels) ?? []
  );
  const [searchParam, setSearchParam] = useUrlParam(SEARCH_PARAM, { replace: true });
  const searchQuery = searchParam ?? '';

  // Chiclets are repeated `attribute=TYPE:id` URL params.
  const [attributeParams, setAttributeParams] = useUrlParam(ATTRIBUTE_PARAM, {
    multiple: true,
    replace: true,
  });
  const chicletRefs = useMemo(
    () => attributeParams.flatMap((v) => parseAttribute(v) ?? []),
    [attributeParams]
  );
  const setChicletRefs = (refs: ChicletRef[], options?: { clearSearch?: boolean }): void =>
    setAttributeParams(refs.map(formatAttribute), {
      clear: options?.clearSearch ? [SEARCH_PARAM] : [],
    });

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Labels/icons for chiclets picked this session; others are hydrated from the API (e.g. after a reload).
  const [chicletInfo, setChicletInfo] = useState<
    Record<string, { text: string; icon?: ReactNode }>
  >({});
  const missingIds = chicletRefs.map((r) => r.id).filter((id) => !chicletInfo[id]);
  const { data: hydrated } = useQuery({
    ...allNounQueryOptions({ query: { id: missingIds, pageSize: missingIds.length } }),
    enabled: missingIds.length > 0,
    staleTime: 60 * 1000,
  });
  const chiclets = useMemo<NounChiclet[]>(() => {
    const byId = new Map((hydrated?.results ?? []).map((n) => [n.id, n]));
    return chicletRefs.map((r) => {
      const info = chicletInfo[r.id];
      if (info) return { ...r, ...info };
      const noun = byId.get(r.id);
      const item = noun ? nounToListItem(noun) : undefined;
      return {
        ...r,
        text: item?.primaryText ?? r.id,
        icon: item?.icon ?? <NounTypeIcon category={nounCategory(r.type)} />,
      };
    });
  }, [chicletRefs, chicletInfo, hydrated]);
  const chicletAttributes = useMemo(
    () => chiclets.map((c) => ({ type: c.type, value: c.id })),
    [chiclets]
  );

  const hasSearchCriteria = debouncedSearch !== '' || chicletAttributes.length > 0;

  const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
    ...allNounQueryInfiniteOptions({
      querySerializer: serializeNounQuery,
      query: {
        search: debouncedSearch || undefined,
        attributes: chicletAttributes.length > 0 ? chicletAttributes : undefined,
        pageSize: PAGE_SIZE,
      },
    }),
    enabled: hasSearchCriteria,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
  });

  const nouns = useMemo(
    () => (hasSearchCriteria ? (data?.pages.flatMap((p) => p.results ?? []) ?? []) : []),
    [data, hasSearchCriteria]
  );
  // Only one chiclet per type is allowed, so other nouns of a selected type are disabled.
  const searchItems: ListViewItem[] = useMemo(() => {
    const selectedTypes = new Set(chicletRefs.map((r) => r.type));
    return nouns
      .filter((n) => n.id)
      .map((n) => {
        const item = nounToListItem(n);
        const typeTaken = n.type !== 'HOTEL' && selectedTypes.has(n.type as NounChiclet['type']);
        return typeTaken ? { ...item, disabled: true } : item;
      });
  }, [nouns, chicletRefs]);

  const showRecent =
    recentHotelIds.length > 0 && searchQuery.trim() === '' && chiclets.length === 0;
  const { data: recentData, isFetching: isRecentFetching } = useQuery({
    ...hotelQueryOptions({ query: { ids: recentHotelIds, pageSize: MAX_RECENT_HOTELS } }),
    enabled: showRecent,
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
  const recentItems: ListViewItem[] = useMemo(() => {
    const byId = new Map((recentData?.results ?? []).filter((h) => h.id).map((h) => [h.id!, h]));
    return recentHotelIds.flatMap((id) => {
      const hotel = byId.get(id);
      return hotel ? [hotelToListItem(hotel)] : [];
    });
  }, [recentData, recentHotelIds]);

  const items = showRecent ? recentItems : searchItems;

  const handleItemClick = (item: ListViewItem): void => {
    const noun = showRecent ? undefined : nouns.find((n) => n.id === item.id);
    if (noun && noun.type !== 'HOTEL') {
      const type = noun.type as NounChiclet['type'];
      setChicletInfo((cur) => ({ ...cur, [item.id]: { text: item.primaryText, icon: item.icon } }));
      if (!chicletRefs.some((r) => r.id === item.id)) {
        setChicletRefs([...chicletRefs, { type, id: item.id }], { clearSearch: true });
      } else {
        setSearchParam('');
      }
      setDebouncedSearch('');
      return;
    }
    setRecentHotelIds((current) => {
      if (current.includes(item.id)) return current;
      const next = [item.id, ...current].slice(0, MAX_RECENT_HOTELS);
      storage.set(OrgStorageKeys.RecentHotels, next);
      return next;
    });
    onSelectHotel(item.id);
  };

  return (
    <ListViewPanel
      items={items}
      searchValue={searchQuery}
      onSearchChange={(value) => setSearchParam(value)}
      isRecent={showRecent}
      onItemClick={handleItemClick}
      onLoadMore={() => void fetchNextPage()}
      hasMore={!showRecent && Boolean(hasNextPage)}
      isLoading={showRecent ? isRecentFetching : isFetching}
      selectedItemId={selectedHotelId}
      searchPlaceholder={chiclets.length > 0 ? 'Continue searching...' : 'Start searching...'}
      chiclets={chiclets}
      onChicletRemove={(id) => setChicletRefs(chicletRefs.filter((r) => r.id !== id))}
      emptyMessage={
        !hasSearchCriteria
          ? 'Start searching...'
          : isError
            ? 'Failed to load results.'
            : 'No results found.'
      }
    >
      {children}
    </ListViewPanel>
  );
}
